/**
 * /Recon portal — Share-create modal (Bucket Q2.5).
 *
 * Buyer hits this from the engagement-detail page on each published
 * report. Modal collects:
 *   - match_mode (4 choices laid out as radio cards with explainers)
 *   - match_value when specific_*
 *   - allow_anonymous when open
 *   - recipient_role
 *   - expires_in_days
 *   - allow_download
 *   - attestation_message
 *
 * On submit, the server returns the cleartext token + a canned share
 * message. Both are surfaced with Copy-to-Clipboard affordances. The
 * cleartext is shown ONCE — closing the modal makes it unrecoverable
 * (re-issuing requires creating a fresh share).
 */
import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { createShareApiV1ClientPortalEngagementsCaseIdReportsReportIdSharesPost } from "@hackereyes/api-client";
import "./share-create-modal.css";

type MatchMode = "bank" | "specific_email" | "specific_domain" | "open";
type RecipientRole = "board" | "it_lead" | "acquirer" | "vendor_risk" | "attorney" | "family" | "other";
type PermissionLevel = "viewer" | "collaborator";

interface CreateShareResponse {
  grant: { id: string };
  cleartext_token: string;
  canned_share_message: string;
  validate_url: string;
}

export function ShareCreateModal({
  caseId,
  reportId,
  reportTitle,
  onClose,
  onCreated,
}: {
  caseId: string;
  reportId: string;
  reportTitle: string;
  onClose: () => void;
  onCreated?: () => void;
}) {
  const [matchMode, setMatchMode] = useState<MatchMode>("bank");
  const [matchValue, setMatchValue] = useState("");
  const [allowAnonymous, setAllowAnonymous] = useState(false);
  const [role, setRole] = useState<RecipientRole>("vendor_risk");
  const [permissionLevel, setPermissionLevel] =
    useState<PermissionLevel>("viewer");
  const [expiresInDays, setExpiresInDays] = useState(90);
  const [allowDownload, setAllowDownload] = useState(true);
  const [attestationMessage, setAttestationMessage] = useState("");
  const [result, setResult] = useState<CreateShareResponse | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const createMutation = useMutation({
    mutationFn: async () => {
      const body: Record<string, unknown> = {
        match_mode: matchMode,
        match_value:
          matchMode === "specific_email" || matchMode === "specific_domain"
            ? matchValue.trim().toLowerCase()
            : null,
        allow_anonymous: matchMode === "open" ? allowAnonymous : false,
        recipient_role: role,
        permission_level: permissionLevel,
        expires_in_days: expiresInDays,
        allow_download: allowDownload,
        attestation_message: attestationMessage.trim() || null,
      };
      const { data, error } =
        await createShareApiV1ClientPortalEngagementsCaseIdReportsReportIdSharesPost({
          path: { case_id: caseId, report_id: reportId },
          body: body as never,
        });
      if (error || !data) {
        const detail = (error as Record<string, unknown>)?.detail as string | undefined;
        throw new Error(detail || "create_failed");
      }
      return data as unknown as CreateShareResponse;
    },
    onSuccess: (data) => {
      setResult(data);
      setErrorMsg(null);
      onCreated?.();
    },
    onError: (e: Error) => setErrorMsg(e.message),
  });

  const valueRequired =
    matchMode === "specific_email" || matchMode === "specific_domain";
  const canSubmit =
    !createMutation.isPending && (!valueRequired || matchValue.trim().length > 0);

  return (
    <div className="scm-backdrop" onClick={onClose}>
      <div
        className="scm-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="scm-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="scm-header">
          <div className="eyebrow">Share · {reportTitle}</div>
          <h2 id="scm-title" className="scm-title">
            {result ? "Share created" : "Generate validation token"}
          </h2>
          <button
            type="button"
            className="scm-close"
            onClick={onClose}
            aria-label="Close"
          >
            ×
          </button>
        </div>

        {result ? (
          <ShareResultView result={result} reportTitle={reportTitle} onClose={onClose} />
        ) : (
          <form
            className="scm-form"
            onSubmit={(e) => {
              e.preventDefault();
              createMutation.mutate();
            }}
          >
            {errorMsg && <div className="scm-error">{errorMsg}</div>}

            <fieldset className="scm-modes">
              <legend className="scm-fieldset-legend">Who can validate?</legend>

              <ModeCard
                value="bank"
                checked={matchMode === "bank"}
                onChange={setMatchMode}
                title="Trust Bank"
                badge="Recommended for TPRM"
                description="Token works for any current entry in your trust bank — adding/archiving entries updates this share automatically."
              />
              <ModeCard
                value="specific_domain"
                checked={matchMode === "specific_domain"}
                onChange={setMatchMode}
                title="Specific domain"
                description="Token works for any email at one domain (e.g., procurement@acmecorp.com)."
              />
              <ModeCard
                value="specific_email"
                checked={matchMode === "specific_email"}
                onChange={setMatchMode}
                title="Specific email"
                description="Token works only for one address. Right for one-off shares (board member, attorney)."
              />
              <ModeCard
                value="open"
                checked={matchMode === "open"}
                onChange={setMatchMode}
                title="Open link"
                description="Token works for anyone who has it. Use sparingly — every viewer's email is logged unless you check 'Allow anonymous'."
              />
            </fieldset>

            {valueRequired && (
              <label className="scm-field">
                <span className="scm-label">
                  {matchMode === "specific_email" ? "Email address" : "Domain"}
                </span>
                <input
                  type="text"
                  className="scm-input"
                  value={matchValue}
                  onChange={(e) => setMatchValue(e.target.value)}
                  placeholder={
                    matchMode === "specific_email"
                      ? "jane@acmecorp.com"
                      : "acmecorp.com"
                  }
                  required
                />
              </label>
            )}

            {matchMode === "open" && (
              <label className="scm-toggle">
                <input
                  type="checkbox"
                  checked={allowAnonymous}
                  onChange={(e) => setAllowAnonymous(e.target.checked)}
                />
                <span>
                  <strong>Allow anonymous</strong> — viewers don't enter an
                  email. True public link. Only use for content you'd be
                  comfortable posting publicly.
                </span>
              </label>
            )}

            <div className="scm-row">
              <label className="scm-field">
                <span className="scm-label">Recipient role</span>
                <select
                  className="scm-input"
                  value={role}
                  onChange={(e) => setRole(e.target.value as RecipientRole)}
                >
                  <option value="vendor_risk">Vendor risk evaluator</option>
                  <option value="board">Board member</option>
                  <option value="it_lead">IT lead</option>
                  <option value="acquirer">M&amp;A acquirer</option>
                  <option value="attorney">Attorney</option>
                  <option value="family">Family member</option>
                  <option value="other">Other</option>
                </select>
              </label>

              <label className="scm-field">
                <span className="scm-label">Expires in (days)</span>
                <input
                  type="number"
                  min={1}
                  max={1825}
                  className="scm-input"
                  value={expiresInDays}
                  onChange={(e) => setExpiresInDays(Number(e.target.value))}
                />
              </label>
            </div>

            <fieldset className="scm-perm">
              <legend className="scm-fieldset-legend">Permission level</legend>
              <PermissionCard
                value="viewer"
                checked={permissionLevel === "viewer"}
                onChange={setPermissionLevel}
                title="Viewer"
                description="Read-only. Recipient can open the report and download a PDF if you allow it below. Default for most TPRM shares."
              />
              <PermissionCard
                value="collaborator"
                checked={permissionLevel === "collaborator"}
                onChange={setPermissionLevel}
                title="Collaborator"
                badge="Comments enabled"
                description="Recipient can post per-finding comments. Use for IT leads or attorneys you want a back-and-forth with. Comments are visible to the assessor, you, and other recipients on the same finding."
              />
            </fieldset>

            <label className="scm-toggle">
              <input
                type="checkbox"
                checked={allowDownload}
                onChange={(e) => setAllowDownload(e.target.checked)}
              />
              <span>Allow PDF download</span>
            </label>

            <label className="scm-field">
              <span className="scm-label">Note to recipient (optional)</span>
              <textarea
                className="scm-input scm-textarea"
                value={attestationMessage}
                onChange={(e) => setAttestationMessage(e.target.value)}
                placeholder="Q1 2026 attestation; renew Q2."
                maxLength={2000}
              />
            </label>

            <div className="scm-actions">
              <button
                type="button"
                className="recon-btn recon-btn-secondary"
                onClick={onClose}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="recon-btn recon-btn-primary"
                disabled={!canSubmit}
              >
                {createMutation.isPending ? "Creating…" : "Generate token"}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

function ShareResultView({
  result,
  reportTitle,
  onClose,
}: {
  result: CreateShareResponse;
  reportTitle: string;
  onClose: () => void;
}) {
  const [copiedToken, setCopiedToken] = useState(false);
  const [copiedMessage, setCopiedMessage] = useState(false);

  const copy = async (text: string, setFlag: (v: boolean) => void) => {
    try {
      await navigator.clipboard.writeText(text);
      setFlag(true);
      setTimeout(() => setFlag(false), 1500);
    } catch {
      // Clipboard API blocked — fall through silently. The textarea is
      // selectable as a manual fallback.
    }
  };

  return (
    <div className="scm-result">
      <p className="scm-result-lede">
        Send the message below to your recipient(s). The token is shown
        only here, only once — closing this dialog makes it
        unrecoverable.
      </p>

      <div className="scm-result-block">
        <div className="scm-result-block-label">Token</div>
        <div className="scm-result-row">
          <code className="scm-result-token">{result.cleartext_token}</code>
          <button
            type="button"
            className="recon-btn recon-btn-secondary scm-copy"
            onClick={() => copy(result.cleartext_token, setCopiedToken)}
          >
            {copiedToken ? "Copied" : "Copy"}
          </button>
        </div>
      </div>

      <div className="scm-result-block">
        <div className="scm-result-block-label">Suggested share message</div>
        <textarea
          className="scm-result-message"
          readOnly
          value={result.canned_share_message}
        />
        <div className="scm-result-row">
          <span className="scm-result-hint">
            Paste into RFP responses, vendor portals, or email.
          </span>
          <button
            type="button"
            className="recon-btn recon-btn-secondary scm-copy"
            onClick={() => copy(result.canned_share_message, setCopiedMessage)}
          >
            {copiedMessage ? "Copied" : "Copy message"}
          </button>
        </div>
      </div>

      <div className="scm-actions">
        <button
          type="button"
          className="recon-btn recon-btn-primary"
          onClick={onClose}
        >
          Done
        </button>
      </div>
      {/* Use the variable so unused-prop warnings don't fire */}
      <span className="scm-sr-only">{reportTitle}</span>
    </div>
  );
}

function ModeCard({
  value,
  checked,
  onChange,
  title,
  badge,
  description,
}: {
  value: MatchMode;
  checked: boolean;
  onChange: (mode: MatchMode) => void;
  title: string;
  badge?: string;
  description: string;
}) {
  return (
    <label className={`scm-mode-card${checked ? " scm-mode-card-checked" : ""}`}>
      <input
        type="radio"
        name="match_mode"
        value={value}
        checked={checked}
        onChange={() => onChange(value)}
      />
      <div className="scm-mode-card-body">
        <div className="scm-mode-card-header">
          <span className="scm-mode-card-title">{title}</span>
          {badge && <span className="scm-mode-card-badge">{badge}</span>}
        </div>
        <div className="scm-mode-card-description">{description}</div>
      </div>
    </label>
  );
}

function PermissionCard({
  value,
  checked,
  onChange,
  title,
  badge,
  description,
}: {
  value: PermissionLevel;
  checked: boolean;
  onChange: (perm: PermissionLevel) => void;
  title: string;
  badge?: string;
  description: string;
}) {
  // Reuses the .scm-mode-card visual language since the choice has the
  // same shape (radio + title + description) as the match-mode picker.
  return (
    <label className={`scm-mode-card${checked ? " scm-mode-card-checked" : ""}`}>
      <input
        type="radio"
        name="permission_level"
        value={value}
        checked={checked}
        onChange={() => onChange(value)}
      />
      <div className="scm-mode-card-body">
        <div className="scm-mode-card-header">
          <span className="scm-mode-card-title">{title}</span>
          {badge && <span className="scm-mode-card-badge">{badge}</span>}
        </div>
        <div className="scm-mode-card-description">{description}</div>
      </div>
    </label>
  );
}
